// @effect-diagnostics globalDate:off -- A card states the instant a spending limit takes effect, as a UTC date (pure, no clock read).
// Can 47 / Can 68: the approval card for changing which models a World can use, in the person's words: which World, which model,
// for whom, what the setting is now and what it will be, and why. One set of words for the Desk's Inbox and Pocket's card. The
// facts are the World's, read when it opened the approval (backend lib/approvalCardFacts.js, gateInstances.cardFacts); the call's
// own arguments say what it will do. Pure: it reads nothing.

import { PROVIDER_CONNECTION_KIND_TITLES } from "@cleo/contracts";

export const SET_MODEL_ACCESS_TOOL = "neucleos_set_model_access";
export const SET_MODEL_ACCESS_GROUP_MEMBER_TOOL = "neucleos_set_model_access_group_member";
export const SET_USAGE_BUDGET_POLICY_TOOL = "neucleos_set_usage_budget_policy";

/** Who asked, as the World recorded it: the caller's name, and whether through Cleo or by the person's own action. */
interface AskedBy {
  readonly askedBy?: string | null;
  readonly via?: "cleo" | "person";
}
export interface ModelAccessCardFacts extends AskedBy {
  readonly kind: "model-access";
  readonly worldName: string | null;
  readonly subjectName?: string | null;
  readonly groupName?: string | null;
  /** The setting at this scope now: on, off, or null when it is not set here (it follows the wider setting). */
  readonly now: boolean | null;
  readonly nowRevision: number;
}
export interface ModelAccessGroupMemberCardFacts extends AskedBy {
  readonly kind: "model-access-group-member";
  readonly worldName: string | null;
  readonly groupName: string | null;
  readonly subjectName: string | null;
  readonly nowInGroup: boolean;
  /** W-ae6c95f2: for an addition, what the group gives, read when the card opened (the access its fence binds). */
  readonly gives?: {
    readonly models: ReadonlyArray<{ readonly provider: string; readonly model: string; readonly enabled: boolean }>;
    readonly kinds: ReadonlyArray<{ readonly kind: string; readonly enabled: boolean }>;
  };
}
/** W-beb3f90f: a World's or a member's AI spending limit: whose it is; the call's own numbers say the limit and when. */
export interface UsageBudgetCardFacts extends AskedBy {
  readonly kind: "usage-budget";
  readonly worldName: string | null;
  readonly scope: "world" | "actor";
  readonly subjectName?: string | null;
}
export type ApprovalCardFacts = ModelAccessCardFacts | ModelAccessGroupMemberCardFacts | UsageBudgetCardFacts;

export interface PlainCard {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
}

/** The facts a card was given, when they are facts these words know; anything else is none. */
export function approvalCardFactsOf(value: unknown): ApprovalCardFacts | null {
  if (value === null || typeof value !== "object") return null;
  const kind = (value as { kind?: unknown }).kind;
  return kind === "model-access" || kind === "model-access-group-member" || kind === "usage-budget" ? (value as ApprovalCardFacts) : null;
}

const quoted = (name: string) => `“${name}”`;
const theWorld = (facts: { worldName: string | null }) => (facts.worldName ? facts.worldName : "this World");
const setting = (value: boolean | null, scope: "world" | "actor" | "group"): string =>
  value === true
    ? "On."
    : value === false
      ? "Off."
      : scope === "world"
        ? "Not set here, so on for everyone by default, unless a group or their own setting says otherwise."
        : scope === "group"
          ? "Not set for this group: its members follow the World's setting or their own."
          : "Not set for them: they follow their groups and the World's setting.";
/** The "Asked by" row, when the World recorded who asked. */
const askedByRow = (facts: AskedBy | null): ReadonlyArray<readonly [string, string]> =>
  facts?.askedBy ? [["Asked by", facts.via === "cleo" ? `${facts.askedBy}, through Cleo.` : `${facts.askedBy}.`]] : [];
const why = (input: Record<string, unknown>) =>
  typeof input["reason"] === "string" && input["reason"].trim() ? input["reason"].trim() : "No reason was given.";

/** A neucleos_set_model_access call in words, or null when it is not one these words describe exactly. */
export function plainSetModelAccess(input: Record<string, unknown>, facts: ModelAccessCardFacts | null): PlainCard | null {
  const scope = input["scope"];
  const enabled = input["enabled"];
  if (scope !== "world" && scope !== "actor" && scope !== "group") return null;
  if (enabled !== true && enabled !== false && enabled !== null) return null;
  if (typeof input["provider"] !== "string" || typeof input["model"] !== "string") return null;
  const model = `${input["provider"]}/${input["model"]}`;
  const world = facts ? theWorld(facts) : "this World";
  const whom =
    scope === "world"
      ? `everyone in ${world}`
      : scope === "actor"
        ? (facts?.subjectName ?? "one member") + ` in ${world}`
        : `the group ${facts?.groupName ? quoted(facts.groupName) : "named in the request"} in ${world}`;
  const headline =
    enabled === true ? `Turn on ${model} for ${whom}` : enabled === false ? `Turn off ${model} for ${whom}` : `Clear the ${model} setting for ${whom}`;
  const forWhom =
    scope === "world"
      ? `Everyone in ${world}, except members or groups with their own setting.`
      : scope === "actor"
        ? `${facts?.subjectName ?? "One member"}, a member of ${world}. Their own setting wins over their groups and the World's.`
        : `The members of the group ${facts?.groupName ? quoted(facts.groupName) : "named in the request"} in ${world}, except members with their own setting.`;
  return {
    headline,
    rows: [
      ["Model", `${model}.`],
      ["For", forWhom],
      ["Now", facts ? setting(facts.now, scope) : "Not read."],
      ["After", setting(enabled, scope)],
      ...askedByRow(facts),
      ["Why", why(input)],
    ],
  };
}

/** A neucleos_set_model_access_group_member call in words, or null when it is not one these words describe exactly. */
export function plainSetModelAccessGroupMember(input: Record<string, unknown>, facts: ModelAccessGroupMemberCardFacts | null): PlainCard | null {
  const active = input["active"];
  if (active !== true && active !== false) return null;
  const who = facts?.subjectName ?? "one member";
  const group = facts?.groupName ? `the model-access group ${quoted(facts.groupName)}` : "a model-access group";
  const world = facts ? theWorld(facts) : "this World";
  return {
    headline: active ? `Add ${who} to ${group} in ${world}` : `Remove ${who} from ${group} in ${world}`,
    rows: [
      ["Who", `${who}, a member of ${world}.`],
      ["Now", facts ? (facts.nowInGroup ? "In the group." : "Not in the group.") : "Not read."],
      ["After", active ? "In the group." : "Not in the group."],
      ...(active && facts?.gives ? [["The group gives", groupGives(facts.gives)] as const] : []),
      [
        "Effect",
        active
          ? "They follow the group's model settings while they are in it, unless they have their own. It gives no World membership or access to anything else."
          : "They stop following the group's model settings; their own and the World's still apply.",
      ],
      ...askedByRow(facts),
      ["Why", why(input)],
    ],
  };
}

/** W-ae6c95f2: what a group gives, model by model and connection kind by connection kind, as the Desk board's card says it. */
function groupGives(gives: NonNullable<ModelAccessGroupMemberCardFacts["gives"]>): string {
  const parts = [
    ...gives.models.map((policy) => `${policy.provider}/${policy.model}: ${policy.enabled ? "on" : "off"}`),
    ...gives.kinds.map((kind) => `${(PROVIDER_CONNECTION_KIND_TITLES as Readonly<Record<string, string>>)[kind.kind] ?? kind.kind}: ${kind.enabled ? "on" : "off"}`),
  ];
  return parts.length ? `${parts.join("; ")}.` : "Nothing yet: the group sets no model or connection policy.";
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** USD micros as dollars, exact to the cent and beyond when it has more: 10000 → "$0.01", 5_000_000_000 → "$5,000.00". */
export function dollarsOfMicros(micros: number): string {
  const cents = Math.floor(micros / 10_000);
  const rest = micros % 10_000;
  const whole = Math.floor(cents / 100).toLocaleString("en-US");
  const fraction = String(cents % 100).padStart(2, "0") + (rest ? String(rest).padStart(4, "0").replace(/0+$/u, "") : "");
  return `$${whole}.${fraction}`;
}
const periodWords = (periodKey: unknown): string | null => {
  const match = typeof periodKey === "string" ? /^(\d{4})-(\d{2})$/u.exec(periodKey) : null;
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  return match && month ? `${month} ${match[1]}` : null;
};
const whenWords = (at: number): string => {
  const d = new Date(at);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}, ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;
};

/** W-beb3f90f: a neucleos_set_usage_budget_policy call (a World's or a member's limit) in words, or null otherwise. */
export function plainSetUsageBudget(input: Record<string, unknown>, facts: UsageBudgetCardFacts | null): PlainCard | null {
  const scope = input["scope"];
  const hard = input["hardLimitMicros"];
  const soft = input["softLimitMicros"];
  const at = input["effectiveAt"];
  const period = periodWords(input["periodKey"]);
  if ((scope !== "world" && scope !== "actor") || typeof hard !== "number" || !Number.isSafeInteger(hard) || hard < 0) return null;
  if (typeof at !== "number" || !Number.isSafeInteger(at) || period === null) return null;
  if (soft !== undefined && (typeof soft !== "number" || !Number.isSafeInteger(soft) || soft < 0)) return null;
  const world = facts ? theWorld(facts) : "this World";
  const who = scope === "world" ? `everyone in ${world}, together` : `${facts?.subjectName ?? "one member"} in ${world}`;
  const whose = scope === "world" ? `${world}'s` : `${facts?.subjectName ?? "one member"}'s`;
  return {
    headline: `Set ${whose} ${period} AI spending limit to ${dollarsOfMicros(hard)}`,
    rows: [
      ["For", `${who}.`],
      ["Limit", `${dollarsOfMicros(hard)} for ${period}: past it, AI work in ${world} on this budget stops until the next month or a higher limit.`],
      ...(typeof soft === "number" ? [["Warning at", `${dollarsOfMicros(soft)}.`] as const] : []),
      ["From", `${whenWords(at)}.`],
      ...askedByRow(facts),
      ["Why", why(input)],
    ],
  };
}

/** The card words for a call by its tool, when these words describe it. */
export function plainModelAccessCall(tool: string, input: Record<string, unknown>, facts: unknown): PlainCard | null {
  const known = approvalCardFactsOf(facts);
  if (tool === SET_MODEL_ACCESS_TOOL) return plainSetModelAccess(input, known?.kind === "model-access" ? known : null);
  if (tool === SET_MODEL_ACCESS_GROUP_MEMBER_TOOL)
    return plainSetModelAccessGroupMember(input, known?.kind === "model-access-group-member" ? known : null);
  if (tool === SET_USAGE_BUDGET_POLICY_TOOL) return plainSetUsageBudget(input, known?.kind === "usage-budget" ? known : null);
  return null;
}
